import React from 'react';
import { useGameStore } from '../../state/gameStore';
import BnBTree from './BnBTree';
import BnBInspector from './BnBInspector';
import BnBGlobalStats from './BnBGlobalStats';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import clsx from 'clsx';

const BnBVisualizer: React.FC = () => {
    const { rootNodeId } = useGameStore(state => state.ui.bnbState);
    const [isVisible, setIsVisible] = React.useState(false);
    const [isCollapsed, setIsCollapsed] = React.useState(false);
    // Initial position at top-left
    const [position, setPosition] = React.useState({ x: 16, y: 16 });
    const dragOffset = React.useRef({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = React.useState(false);

    // Auto-show when B&B starts and set to default position
    React.useEffect(() => {
        if (rootNodeId) {
            setIsVisible(true);
            setPosition({ x: 16, y: 16 });
        }
    }, [rootNodeId]);

    // Drag handlers
    React.useEffect(() => {
        if (!isDragging) return;
        const handleMove = (e: MouseEvent) => {
            setPosition({
                x: e.clientX - dragOffset.current.x,
                y: e.clientY - dragOffset.current.y
            });
        };
        const handleUp = () => setIsDragging(false);
        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
        return () => {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
        };
    }, [isDragging]);

    const startDrag = (e: React.MouseEvent) => {
        if (e.button !== 0) return;
        dragOffset.current = { x: e.clientX - position.x, y: e.clientY - position.y };
        setIsDragging(true);
    };

    if (!isVisible || !rootNodeId) return null;

    return (
        <div className={clsx(
            "absolute z-30 transition-all duration-300 ease-out flex flex-col shadow-2xl border border-white/10 backdrop-blur-md bg-slate-900/90 rounded-xl overflow-hidden",
            isCollapsed ? "w-64 h-10" : "w-[1000px] h-[600px]"
        )}
            style={{ left: position.x, top: position.y }}
        >
            {/* Header / Drag Handle */}
            <div
                className={clsx(
                    "h-8 flex items-center justify-between px-4 bg-white/5 cursor-grab hover:bg-white/10 transition-colors select-none",
                    isDragging && "cursor-grabbing"
                )}
                onMouseDown={startDrag}
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <span className="text-xs font-bold text-slate-400 tracking-wider flex items-center gap-2">
                    🌳 Branch & Bound
                </span>
                <div className="flex items-center gap-2">
                    {isCollapsed ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
                    <button
                        onClick={(e) => { e.stopPropagation(); setIsVisible(false); }}
                        className="text-slate-500 hover:text-slate-200"
                    >
                        <X size={14} />
                    </button>
                </div>
            </div>

            {/* Content Area */}
            {!isCollapsed && (
                <div className="flex-1 flex relative">
                    <div className="absolute left-0 right-0 top-0 z-10">
                        <BnBGlobalStats />
                    </div>
                    {/* Left: Inspector (Compact) */}
                    <div className="w-72 border-r border-white/5 bg-black/20 p-3 pt-14 flex flex-col justify-center flex-shrink-0">
                        <BnBInspector />
                    </div>

                    {/* Right: Tree Visualization */}
                    <div className="flex-1 bg-black/40 relative overflow-hidden pt-10">
                        <BnBTree />
                    </div>
                </div>
            )}
        </div>
    );
};

export default BnBVisualizer;
